import { useEffect, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { Avatar, Box, Typography } from "@mui/material";
import SingleSelectDropdown from "../../../Components/CustomDropdowns/SingleSelectDropdown";
import { layoutStyles, previewBadge } from "../styles/styles";
import { useTranslation } from "react-i18next";
import emitter from "../../../Utils/events/eventBus";
import { apiPostRequest } from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";
import KycStatusChip from "../../../Components/KycStatusChip";

const SidebarHeader = (props) => {
  const { isCommunityManagementRole, sidebarData, userCommunities, isMoMView } = props;
  const navigate = useNavigate();
  const { t } = useTranslation("routes-and-main-menu");
  const [communityName, setCommunityName] = useState("");
  const [communityImage, setCommunityImage] = useState("");
  const [supplierImage, setSupplierImage] = useState("");
  const [selectedCommunity, setSelectedCommunity] = useState(
    sidebarData?.communityName,
  );

  useEffect(() => {
    console.log("sidebarData", sidebarData);
    if (sidebarData?.communityName) {
      setCommunityName(sidebarData.communityName);
    }
    if (sidebarData?.communityImage) {
      setCommunityImage(sidebarData.communityImage);
    }
    if (sidebarData?.supplierImage) {
      setSupplierImage(sidebarData.supplierImage);
    }
  }, [sidebarData]);

  useEffect(() => {
    const handleCommunityUpdated = (name) => {
      setCommunityName(name);
    };

    emitter.on("community-name-updated", handleCommunityUpdated);

    const handleCommunityImageUpdated = (imageUrl) => {
      setCommunityImage(imageUrl);
    };

    emitter.on("community-image-updated", handleCommunityImageUpdated);

    const handleSupplierImageUpdated = (imageUrl) => {
      setSupplierImage(imageUrl);
    };

    emitter.on("supplier-image-updated", handleSupplierImageUpdated);

    // The sidebar avatar reads `profiles.image_uid` first (see
    // Admin_system/src/services/sidebarService.ts), so a profile-picture
    // change from MyProfile should also update the avatar immediately. We
    // reuse the same `supplierImage` state — semantically the sidebar
    // doesn't care whether the URL came from the profile or the business
    // row, it's just "the current owner's avatar".
    const handleProfileImageUpdated = (imageUrl) => {
      setSupplierImage(imageUrl ?? "");
    };

    emitter.on("profile-image-updated", handleProfileImageUpdated);

    return () => {
      emitter.off("community-name-updated", handleCommunityUpdated);
      emitter.off("community-image-updated", handleCommunityImageUpdated);
      emitter.off("supplier-image-updated", handleSupplierImageUpdated);
      emitter.off("profile-image-updated", handleProfileImageUpdated);
    };
  }, []);

  const handleCommunityChange = async (communityName) => {
    const community = userCommunities.find(
      (community) => community.name === communityName,
    );
    try {
      await apiPostRequest(endpoints.switchCommunity, {
        communityId: community.id,
      });
      setSelectedCommunity(communityName);
      navigate(0);
    } catch (error) {
      console.error("Failed to switch community:", error);
    }
  };

  const {
    sidebarHeader,
    sidebarTitle,
    logoContainer,
    logoText,
    communityDropdown,
    communityPill,
    communityPillText,
    menuItem,
    menuItemText,
  } = layoutStyles;

  // With a single community (or none loaded yet) there is nothing to switch
  // to, so we render a static pill instead of the dropdown — no arrow, no menu.
  const hasMultipleCommunities = userCommunities?.length > 1;
  const singleCommunity = userCommunities?.[0];
  // An MCM ("MoM", Keilot-granted, from the sidebar API) has a portfolio even
  // when they manage only one community directly — `userCommunities` lists
  // just the ones they are CM/moderator of.
  const isMoM = sidebarData?.isMoM === true;

  return (
    <Box sx={sidebarHeader}>
      <Typography sx={sidebarTitle}>
        {isMoMView
          ? t("main_manager")
          : isCommunityManagementRole
            ? t("community_management")
            : t("supplier_management")}
      </Typography>

      {/* Portfolio entries — sit ABOVE the community switcher for a CM who
          manages more than one community (or is an MCM), and are the ONLY nav
          in MoM view. */}
      {(isMoMView || (isCommunityManagementRole && (hasMultipleCommunities || isMoM))) && (
        <>
          <NavLink to="/communities" style={{ textDecoration: "none" }}>
            {({ isActive }) => (
              <Box sx={{ ...menuItem(isActive), mt: "16px" }}>
                <img
                  src="/icons/sidebar-icons/manage-community.svg"
                  alt=""
                  style={{ width: "20px", height: "20px" }}
                />
                <Typography sx={menuItemText}>
                  {t("my_communities.menu_item")}
                </Typography>
              </Box>
            )}
          </NavLink>
          {/* Manager-of-Managers hub — right below the portfolio dashboard,
              for MCMs only. */}
          {(isMoMView || isMoM) && (
            <NavLink to="/managers-hub" style={{ textDecoration: "none" }}>
              {({ isActive }) => (
                <Box sx={{ ...menuItem(isActive), mt: "8px" }}>
                  <img
                    src="/icons/sidebar-icons/manage-community.svg"
                    alt=""
                    style={{ width: "20px", height: "20px" }}
                  />
                  <Typography sx={{ ...menuItemText, whiteSpace: "normal", minWidth: 0 }}>
                    {t("managers_hub.menu_item")}
                  </Typography>
                  {/* FE-first mock — preview badge until the backend lands. */}
                  <Box sx={{ ...previewBadge, flexShrink: 0 }}>
                    {t("bottom-menu-and-settings:preview")}
                  </Box>
                </Box>
              )}
            </NavLink>
          )}
          <Box sx={{ borderTop: "1px solid rgba(234, 234, 234, 1)", mt: "16px" }} />
        </>
      )}

      {!isMoMView && (!isCommunityManagementRole ? (
        <Box sx={logoContainer}>
          <Avatar
            alt={
              isCommunityManagementRole
                ? communityName?.toUpperCase()
                : sidebarData.supplierName?.toUpperCase()
            }
            src={isCommunityManagementRole ? communityImage : supplierImage}
            sx={{ width: 40, height: 40 }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
            <Typography sx={logoText}>
              {isCommunityManagementRole ? communityName : sidebarData.supplierName}
            </Typography>
            {/* Where the supplier stands with Grow's business verification.
                Nothing renders once it's approved. */}
            <KycStatusChip sx={{ alignSelf: "flex-start" }} />
          </Box>
        </Box>
      ) : !hasMultipleCommunities ? (
        <Box sx={logoContainer}>
          <Box sx={communityPill}>
            <Avatar
              alt={(communityName || singleCommunity?.name)?.toUpperCase()}
              src={communityImage || singleCommunity?.image_url}
              sx={{ width: 38, height: 38 }}
            />
            <Typography sx={communityPillText}>
              {communityName || singleCommunity?.name}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={logoContainer}>
          <SingleSelectDropdown
            value={selectedCommunity}
            onChange={handleCommunityChange}
            options={userCommunities.map((community) => ({
              value: community.name,
              label: community.name,
              imgUrl: community.image_url,
            }))}
            showOptionImg={true}
            horizontalAnchorOrigin="right"
            buttonHeight="48px"
            sx={communityDropdown}
          />
        </Box>
      ))}
    </Box>
  );
};

export default SidebarHeader;
